import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { getStyles } from './useStyles'
import { MenuOption } from './MenuOption'
import { SideNavLink } from './SideNavLink'
import { useLocation, useNavigate } from 'react-router-dom'
import { TextAvatar } from './TextAvatar'
import { useGetSettingsActive } from '@user/components/Settings/hooks'

import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DashboardOutlined from '@mui/icons-material/DashboardOutlined'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import MonetizationOnOutlined from '@mui/icons-material/MonetizationOnOutlined'
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined'
import PersonAddAlt1Outlined from '@mui/icons-material/PersonAddAlt1Outlined'
import Drawer, { drawerClasses } from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import MenuIcon from '@mui/icons-material/Menu'
import Stack from '@mui/material/Stack'

import { useCheckPermissions } from '@user/store/authorization/permissions'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { IconAvatar } from './IconAvatar'
import { SalesFunnelMenuOption } from './SalesFunnelMenuOption'
import { useCampaignTrackingEnabled } from '@user/features/CampaignTracking'
import { ActivitySummary } from '@user/features/Activity/ActivitySummary'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { MapView } from '@user/features/MapView/MapView'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useUnifiedCalendarEnabled } from '@user/store/tenantFeatures/hooks/useUnifiedCalendarEnabled'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'

const calendarUrl = '/calendar'

export function SideNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isSettingsMode } = useGetSettingsActive()

  const { isAdmin } = useSelector(selectCurrentUser)
  const currentUser = useSelector(selectCurrentUser)
  const isGuestUser = currentUser?.roleName === guestRoleName
  const [ASBatchContent, setAsBatchContent] = React.useState<number>(0)

  const [canViewLeads, canViewOpps, canViewPendingLeads] = useCheckPermissions(
    'leads_view',
    'opportunities_view',
    'pending_leads_view',
  )

  const handleLinkClick = useCallback(
    (url: string) => {
      if (url != location.pathname) {
        navigate(url)
      }
    },
    [navigate, location.pathname],
  )
  const pathname = location.pathname

  const campaignTrackingEnabled = useCampaignTrackingEnabled()
  const mapViewEnabled = useMapViewFeatureEnabled()
  const unifiedCalendarEnabled = useUnifiedCalendarEnabled()

  const salesCenterLinks = useMemo(
    () =>
      [
        {
          label: 'Dashboard',
          url: '/scdashboard',
          icon: <DashboardOutlined />,
          isAllowed: true,
          dataTestId: 'salesCenterDashboardMenuOption',
        },
        {
          label: 'Pending Leads',
          url: '/pending-leads',
          icon: <PersonAddAlt1Outlined />,
          isAllowed: !isGuestUser && canViewPendingLeads,
          dataTestId: 'salesCenterPendingLeadsMenuOption',
        },
        {
          label: 'Leads',
          url: '/leads',
          icon: <PeopleAltOutlined />,
          isAllowed: !isGuestUser && canViewLeads,
          dataTestId: 'salesCenterLeadsMenuOption',
        },
        {
          label: 'Opportunities',
          url: '/opportunities',
          icon: <MonetizationOnOutlined />,
          isAllowed: !isGuestUser && canViewOpps,
          dataTestId: 'salesCenterOpportunitiesMenuOption',
        },
      ].filter((link) => link.isAllowed),
    [isGuestUser, canViewPendingLeads, canViewLeads, canViewOpps],
  )

  //#region open/close state
  const [expanded, setExpanded] = useState(false)
  const onClickMenu = useCallback(() => setExpanded((e) => !e), [])

  const triggerResize = useCallback(() => {
    void window.dispatchEvent(new Event('resize'))
  }, [])
  //#endregion
  const styles = getStyles(useTheme())
  return (
    <Drawer
      variant="permanent"
      sx={{
        ...styles.drawer,
        ...(expanded ? styles.drawerOpen : styles.drawerClose),
        [`.${drawerClasses.paper}`]: {
          ...styles.drawerPaper,
          ...(expanded ? styles.drawerOpen : styles.drawerClose),
        },
      }}
      onTransitionEnd={triggerResize}
    >
      <Box sx={styles.menuBtnContainer}>
        <IconButton
          onClick={onClickMenu}
          sx={styles.iconContainer}
          size="large"
          data-test-id="sideNavToggleBtn"
        >
          {expanded ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>
      <List component="nav" sx={{ flexGrow: 1, minHeight: 0 }}>
        {!isSettingsMode ? (
          <>
            {salesCenterLinks.map((link) => (
              <SideNavLink
                key={link.url}
                label={link.label}
                url={link.url}
                icon={link.icon}
                expanded={expanded}
                // `/leads` and `/opportunities` are prefix routes, so a detail
                // page such as `/leads/123` must keep its nav item lit.
                selected={
                  pathname === link.url || pathname.startsWith(`${link.url}/`)
                }
                handleLinkClick={handleLinkClick}
                dataTestId={link.dataTestId}
              />
            ))}
          </>
        ) : (
          <>
            <MenuOption
              dataTestId={'settingsHomeMenuOptionSettingsHome'}
              expanded={expanded}
              handleLinkClick={handleLinkClick}
              location={pathname}
              label="Settings Home"
              icon={<IconAvatar icon={<HomeOutlined fontSize="inherit" />} />}
              options={[
                {
                  label: 'Settings Home',
                  url: '/settings/home',
                  isAllowed: isAdmin,
                  dataTestId: 'settingsHomeMenuOption',
                },
              ]}
            />
            <MenuOption
              dataTestId={'salesCenterMenuOptionWWMarketing'}
              expanded={expanded}
              handleLinkClick={handleLinkClick}
              location={pathname}
              label="WW Marketing"
              icon={<TextAvatar text="WM" />}
              options={[
                {
                  label: 'Company Setup',
                  url: '/settings/companySetup',
                  isAllowed: isAdmin,
                  dataTestId: 'companySetupMenuOption',
                },
                {
                  label: 'Roles & Permissions',
                  url: '/settings/roles',
                  isAllowed: isAdmin,
                  dataTestId: 'rolesPermissionsMenuOption',
                },
                {
                  label: 'Users & Access',
                  url: '/settings/users',
                  isAllowed: isAdmin,
                  dataTestId: 'usersAccessMenuOption',
                },
                {
                  label: 'Notifications',
                  url: '/settings/notifications',
                  isAllowed: isAdmin,
                  dataTestId: 'notificationsMenuOption',
                },
                {
                  label: 'New Lead/Opportunity Field Settings',
                  url: '/settings/formSettings',
                  isAllowed: isAdmin,
                  dataTestId: 'formSettingsMenuOption',
                },
                {
                  label: 'Activities',
                  url: '/settings/activities',
                  isAllowed: isAdmin,
                  dataTestId: 'activitiesMenuOption',
                },
                {
                  label: 'Campaign Tracking',
                  url: '/settings/campaignTracking',
                  isAllowed: isAdmin && campaignTrackingEnabled,
                  dataTestId: 'campaignTrackingMenuOption',
                },
                {
                  label: 'Import Utility',
                  url: '/settings/importUtility',
                  isAllowed: true,
                  dataTestId: 'importUtilityMenuOption',
                },
                {
                  label: 'API Settings',
                  url: '/settings/apiSettings',
                  isAllowed: isAdmin,
                  dataTestId: 'apiSettingsMenuOption',
                },
              ]}
            />
            <MenuOption
              dataTestId={'salesCenterMenuOptionSalesCenter'}
              expanded={expanded}
              handleLinkClick={handleLinkClick}
              location={pathname}
              label="Sales Center"
              icon={<TextAvatar text="SC" />}
              options={[
                {
                  label: 'Sales Funnels',
                  url: '/settings/salesFunnels',
                  isAllowed: isAdmin,
                  dataTestId: 'salesFunnelsMenuOption',
                },
                {
                  label: 'Sales Teams',
                  url: '/settings/salesTeams',
                  isAllowed: isAdmin,
                  dataTestId: 'salesTeamsMenuOption',
                },
                {
                  label: 'Custom Fields',
                  url: '/settings/customFields',
                  isAllowed: true,
                  dataTestId: 'customFieldsMenuOption',
                },
                {
                  label: 'Assignment Rules',
                  url: '/settings/assignmentRules',
                  isAllowed: true,
                  dataTestId: 'assignmentRulesMenuOption',
                },
                {
                  label: 'Notes',
                  url: '/settings/notes',
                  isAllowed: true,
                  dataTestId: 'notesMenuOption',
                },
              ]}
            />
          </>
        )}
      </List>
      {!isGuestUser && (
        <Stack flexShrink={0} flexDirection="column">
          <SalesFunnelMenuOption
            batchContent={ASBatchContent}
            expanded={expanded}
          />
          <ActivitySummary
            onBatchContentFetched={setAsBatchContent}
            expanded={expanded}
          />
          {mapViewEnabled && (canViewLeads || canViewOpps) && (
            <MapView expanded={expanded} />
          )}
          {unifiedCalendarEnabled && (
            <SideNavLink
              label="Calendar"
              url={calendarUrl}
              icon={<CalendarTodayOutlined />}
              expanded={expanded}
              selected={
                pathname === calendarUrl ||
                pathname.startsWith(`${calendarUrl}/`)
              }
              handleLinkClick={handleLinkClick}
              dataTestId="salesCenterCalendarMenuOption"
            />
          )}
        </Stack>
      )}
    </Drawer>
  )
}
